import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomer } from "../../contexts/CustomerContext";
import AppLayout from "../../Components/AppLayout";
import LocationCard from "./LocationCard";
import BusinessLoyaltyCard from "./BusinessLoyaltyCard";
import { SortMode, usePortalSections } from "./usePortalSections";
import PortalSignIn from "./PortalSignIn";
import styles from "./Portal.module.scss";
import BusinessIcon from "@mui/icons-material/Business";

const HIDDEN_KEY = "sally_hidden_locations";

const readHiddenIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const PortalScreen: React.FC = () => {
  const {
    authState,
    customer,
    locations,
    dataLoading,
    error,
    noProfile,
    refreshProfile,
  } = useCustomer();
  const navigate = useNavigate();

  const [sortMode, setSortMode] = useState<SortMode>("recent");
  const [hiddenIds, setHiddenIds] = useState<string[]>(readHiddenIds);
  const [hiddenExpanded, setHiddenExpanded] = useState(false);
  const { groups, standalone, hidden } = usePortalSections(sortMode, hiddenIds);

  const hideLocation = (id: string) => {
    const next = [...hiddenIds, id];
    setHiddenIds(next);
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
  };

  const unhideLocation = (id: string) => {
    const next = hiddenIds.filter((i) => i !== id);
    setHiddenIds(next);
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
  };

  useEffect(() => {
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authState === "authenticated" && noProfile) navigate("/signup");
  }, [authState, noProfile, navigate]);

  if (authState === "loading" || (authState === "authenticated" && noProfile)) {
    return (
      <div className={styles.SignInPage}>
        <div className={styles.CenteredWrap}>
          <div className={styles.Spinner} />
          <span>Just a sec...</span>
        </div>
      </div>
    );
  }

  if (authState === "unauthenticated") return <PortalSignIn />;

  return (
    <AppLayout
      subtitle="Welcome back"
      title={
        customer ? `${customer.firstName} ${customer.lastName}` : undefined
      }
    >
      {error && <div className={styles.ErrorBanner}>{error}</div>}

      {dataLoading ? (
        <div className={styles.CenteredWrap}>
          <div className={styles.Spinner} />
        </div>
      ) : locations.length === 0 ? (
        <div className={styles.EmptyState}>
          <div className={styles.EmptyIcon}>
            <BusinessIcon />
          </div>
          <p className={styles.EmptyTitle}>No venues yet</p>
          <p className={styles.EmptyBody}>
            Scan a QR code at a venue to start earning rewards.
          </p>
        </div>
      ) : (
        <>
          <div className={styles.SortRow}>
            <p className={styles.SectionTitle}>Your locations</p>
            <div className={styles.SortToggle}>
              <button
                className={`${styles.SortBtn} ${sortMode === "recent" ? styles.SortBtnActive : ""}`}
                onClick={() => setSortMode("recent")}
                type="button"
              >
                Last visited
              </button>
              <button
                className={`${styles.SortBtn} ${sortMode === "az" ? styles.SortBtnActive : ""}`}
                onClick={() => setSortMode("az")}
                type="button"
              >
                A–Z
              </button>
            </div>
          </div>
          {groups.map((group) => (
            <div
              key={group.business.businessId}
              className={styles.BusinessGroup}
            >
              <BusinessLoyaltyCard group={group} />
              <div className={styles.Grid}>
                {group.locations.map((loc) => (
                  <LocationCard
                    key={loc.id}
                    location={loc}
                    showLoyalty={false}
                    onHide={() => hideLocation(loc.id)}
                  />
                ))}
              </div>
            </div>
          ))}

          {standalone.length > 0 && (
            <div className={styles.Grid}>
              {standalone.map((loc) => (
                <LocationCard
                  key={loc.id}
                  location={loc}
                  onHide={() => hideLocation(loc.id)}
                />
              ))}
            </div>
          )}

          {hidden.length > 0 && (
            <div className={styles.HiddenSection}>
              <button
                className={styles.HiddenToggle}
                type="button"
                onClick={() => setHiddenExpanded((v) => !v)}
              >
                <span>Hidden venues ({hidden.length})</span>
                <span
                  className={`${styles.HiddenChevron} ${hiddenExpanded ? styles.HiddenChevronOpen : ""}`}
                >
                  ›
                </span>
              </button>
              {hiddenExpanded && (
                <div className={styles.Grid}>
                  {hidden.map(({ location: loc, sharedLoyalty }) => (
                    <LocationCard
                      key={loc.id}
                      location={loc}
                      hidden
                      showLoyalty={!sharedLoyalty}
                      onUnhide={() => unhideLocation(loc.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
};

export default PortalScreen;
